import { Router } from '@angular/router';
import { map, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Component, effect, input } from '@angular/core';

import { OrderKeys } from '@config/keys/order.keys';
import { ObjectUtils } from '@app/utilities/object.util';
import { DoodOrderModel } from '@store/order/order.model';
import { DateUtils } from '@shared/utils/date/date.utils';
import { OrderStatusLabels } from '@config/labels/order.labels';
import { OrderStatusValues, OrderTypeValues } from '@config/values/order.values';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { DateHelperService } from 'src/app/core/services/date-helper/date-helper.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { AnalyticsService } from '@core/services/app/analytics.service';

/** Filled text replaces the default label. An explicit empty string hides the line. */
export function resolveOnSiteCreatedAtLabel(
  value: unknown,
  locale: string,
  defaultLabel: string,
): string | null {
  if (value == null) {
    return defaultLabel;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return defaultLabel;
  }
  const record = value as Record<string, unknown>;
  if ('$cases' in record || '$param' in record) {
    return defaultLabel;
  }
  const localeText = record[locale];
  if (typeof localeText !== 'string') {
    return defaultLabel;
  }
  const trimmed = localeText.trim();
  return trimmed ? trimmed : null;
}

@Component({
  selector: 'app-order-status-atom-v2',
  templateUrl: './order-status-atom-v2.component.html',
  standalone: false,
})
export class OrderStatusAtomV2Component {
  readonly shopName = input.required<string>();
  readonly orderNumber = input.required<string>();
  readonly orderStatus = input.required<string>();
  readonly order = input.required<DoodOrderModel>();
  readonly displayOrderStatus = input(true);
  readonly onSiteTimeSource = input<'created_at' | 'ready_at'>('created_at');
  readonly onSiteCreatedAtLabel = input<unknown>(undefined);
  readonly onSiteReadyAtLabel = input<unknown>(undefined);
  readonly displayLocalizeButton = input(true);
  readonly displayDetailsButton = input(true);
  readonly displayCustomButton = input(false);
  readonly customButtonText = input('');
  readonly customButtonUrl = input('');
  readonly locationName = input('');
  readonly textColor = input<IContentBuilderFieldColor>({
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  });
  readonly secondaryTextColor = input<IContentBuilderFieldColor>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });

  orderStatusValues = OrderStatusValues;
  orderStatusLabels = OrderStatusLabels;

  orderTypeValues = OrderTypeValues;

  readyAt?: string;
  createdAt?: string;
  estimatedArrival?: string;
  daysUntilReadyString?: string;
  daysUntilCreatedString?: string;
  daysUntilEstimatedTimeOfArrivalString?: string;

  constructor(
    private readonly router: Router,
    private settingsSelector: SettingsStoreSelector,
    private readonly routerHelper: RouterHelperService,
    private readonly translateService: TranslateService,
    private readonly dateHelperService: DateHelperService,
    private readonly analyticsService: AnalyticsService,
  ) {
    effect(() => {
      this.updateOrderDerivedValues(this.order());
    });
  }

  private updateOrderDerivedValues(order: DoodOrderModel): void {
    this.translateService
      .get('order.format')
      .pipe(
        take(1),
        map(translation => {
          this.createdAt = this.getFormattedDateString(order.created_at, translation);
          this.readyAt = this.getFormattedDateString(order.ready_at, translation);
          this.estimatedArrival = this.getFormattedDateString(
            order[OrderKeys.EstimatedTimeOfArrival],
            translation,
          );
          this.dateHelperService
            .getDaysUntilString(order.created_at)
            .pipe(
              take(1),
              tap(daysUntilString => (this.daysUntilCreatedString = daysUntilString)),
            )
            .subscribe();
          this.dateHelperService
            .getDaysUntilString(order.ready_at)
            .pipe(
              take(1),
              tap(daysUntilString => (this.daysUntilReadyString = daysUntilString)),
            )
            .subscribe();
          this.dateHelperService
            .getDaysUntilString(order[OrderKeys.EstimatedTimeOfArrival])
            .pipe(
              take(1),
              tap(
                daysUntilString => (this.daysUntilEstimatedTimeOfArrivalString = daysUntilString),
              ),
            )
            .subscribe();
        }),
      )
      .subscribe();
  }

  resolvedOnSiteTimeLine(): {
    label: string;
    day: string | undefined;
    time: string | undefined;
  } | null {
    const source = this.onSiteTimeSource() === 'ready_at' ? 'ready_at' : 'created_at';
    const label = resolveOnSiteCreatedAtLabel(
      source === 'ready_at' ? this.onSiteReadyAtLabel() : this.onSiteCreatedAtLabel(),
      this.settingsSelector.app.locale,
      this.translateService.instant(
        source === 'ready_at' ? 'order.withdrawal-time' : 'order.created_at',
      ),
    );
    if (!label) {
      return null;
    }
    if (source === 'ready_at') {
      return { label, day: this.daysUntilReadyString, time: this.readyAt };
    }
    return { label, day: this.daysUntilCreatedString, time: this.createdAt };
  }

  goToOrderDetail(order: DoodOrderModel): void {
    this.router.navigate([this.routerHelper.translateRoute(`/orders/${order.id}/details`)]);
  }

  locateShop(order: DoodOrderModel): void {
    const { device } = this.settingsSelector.device;
    if (device === 'desktop') {
      if (order.pickup_location?.point?.coordinates) {
        window.open(
          `https://maps.google.com/?q=${order[OrderKeys.PickupLocation]?.point?.coordinates?.[0]},${order[OrderKeys.PickupLocation]?.point?.coordinates?.[1]}&amp;ll=`,
        );
      } else {
        window.open(
          `https://maps.google.com/?q=${order[OrderKeys.PickupLocation]?.street_line1}+${order[OrderKeys.PickupLocation]?.post_code}+${order[OrderKeys.PickupLocation]?.city}`,
        );
      }
    } else {
      if (order[OrderKeys.PickupLocation]?.point?.coordinates) {
        window.open(
          `maps://maps.google.com/maps?daddr=${order[OrderKeys.PickupLocation]?.point?.coordinates?.[0]},${order[OrderKeys.PickupLocation]?.point?.coordinates?.[1]}&amp;ll=`,
        );
      } else {
        window.open(
          `maps://maps.google.com/maps?daddr=${order[OrderKeys.PickupLocation]?.street_line1}+${order[OrderKeys.PickupLocation]?.post_code}+${order[OrderKeys.PickupLocation]?.city}`,
        );
      }
    }
  }

  // TODO: Check if date parameter can be undefined
  private getFormattedDateString(
    date: Date | string | undefined | null,
    format: string,
  ): string | undefined {
    if (ObjectUtils.isNotNil(date)) {
      return format !== 'order.format'
        ? DateUtils.dayjsInMarketplaceTimezone(date).format(format)
        : DateUtils.dayjsInMarketplaceTimezone(date).format('HH:mm');
    }
  }

  goToDeliveryTrackingUrl(url: string): void {
    window.open(url);
  }

  forceType(event: any): any {
    return event as any;
  }
}
